<script setup lang="ts">
import {computed, inject, useTemplateRef, watch, ref, onMounted} from "vue";
import {useRoute, useRouter} from "vue-router";
import {getGroupQuestionResponseVotes, voteOnGroupQuestionResponse} from "@/Services/group-question-responses.ts";
import useEcho from "@/Composables/echo.ts";
import User from "@/Models/User.ts";
import VotingResponse from "./VotingResponse.vue";
import Question from "@/Models/Question.ts";
import {useIntersectionObserver, useStorage} from "@vueuse/core";
import {Icon} from "@iconify/vue";

const $date = inject("date") as any;
const $route = useRoute();
const router = useRouter();

const commentIdFromUrl = computed(() => $route.query.comment_id as string | undefined);
const responseWithHighlightedComment = ref<string | number | null>(null);
const highlightCommentId = ref<string | number | null>(null);

const findResponseWithComment = (commentId: string | number) => {
    for (const response of props.question.responses || []) {
        // Check top-level comments
        const topLevelMatch = response.comments?.find((c: any) => String(c.id) === String(commentId));
        if (topLevelMatch) {
            return response.id;
        }
        // Check replies
        for (const comment of response.comments || []) {
            const replyMatch = comment.replies?.find((r: any) => String(r.id) === String(commentId));
            if (replyMatch) {
                return response.id;
            }
        }
    }
    return null;
};

onMounted(() => {
    if (commentIdFromUrl.value) {
        const responseId = findResponseWithComment(commentIdFromUrl.value);
        if (responseId) {
            responseWithHighlightedComment.value = responseId;
            highlightCommentId.value = commentIdFromUrl.value;
        }
    }
});

const props = defineProps<{
    question: Question;
    disabled?: boolean;
    sort: "date" | "votes" | "page" | "person" | "comments";
    hasFilters?: boolean;
}>();
console.log(props);
const emit = defineEmits<{
    vote: any;
    votesChanged: any;
    responseClick: any;
    edit: any;
    delete: any;
    comment: any;
    deleteComment: any;
    pageHeadingInView: [
        {
            index: number;
            element: HTMLElement;
            isIntersecting: boolean;
            entry: IntersectionObserverEntry;
        },
    ];
}>();
const collapsed = ref([]);
const pageHeadings = useTemplateRef("pageHeadings");

// Set up intersection observer for page headings
watch(
    pageHeadings,
    (headings) => {
        if (headings && headings.length) {
            headings.forEach((heading, index) => {
                const {stop} = useIntersectionObserver(
                    heading,
                    ([entry]) => {
                        // console.log(heading, entry);
                        // Emit event both when entering and leaving viewport
                        emit("pageHeadingInView", {
                            index,
                            element: heading,
                            isIntersecting: entry.isIntersecting,
                            entry,
                        });
                    },
                    {threshold: 0.5},
                );
            });
        }
    },
    {deep: true},
);
const getVoteCount = (response: any): number =>
    Object.values(response.data?.votes ?? {}).reduce((acc: number, vote: number) => acc + vote, 0);

const responses = computed(() => {
    console.log(props.sort);
    if (props.sort === "page" && props.question.pageCount > 1) {
        let groupedResponses: {[key: string]: any[]} = {};
        props.question.stimuli?.[0]?.files.forEach((_: any, i: number) => {
            groupedResponses[i] = props.question?.responses.filter((R: any) => +R.data.annotation?.pageIndex === +i);
        });
        return groupedResponses;
    }

    if (props.sort === "votes") {
        return props.question?.responses.toSorted((a: any, b: any) => getVoteCount(b) - getVoteCount(a));
    }

    if (props.sort === "comments") {
        return props.question?.responses.toSorted(
            (a: any, b: any) => (b.comments_count || 0) - (a.comments_count || 0),
        );
    }

    if (props.sort === "person") {
        return props.question?.responses.toSorted((a: any, b: any) => {
            // Sort by user name if available, otherwise by user_id
            const aName = a.user?.name || a.user?.profile?.name || "";
            const bName = b.user?.name || b.user?.profile?.name || "";
            return aName.localeCompare(bName);
        });
    }

    return props.question?.responses;
});

const expandAllComments = ref(responses.value.length > 0 && responses.value.every((R: any) => R.comments_count > 0));

const vote = async (response, vote: number) => {
    try {
        const resp = await voteOnGroupQuestionResponse(
            router.currentRoute.value.params.group,
            router.currentRoute.value.params.question,
            response.id,
            vote,
        );
        emit("vote", resp);
    } catch (error) {
        console.error("Voting failed:", error);
    }
};

const responseRefs = useTemplateRef("responseRefs");

const $echo = useEcho([
    {
        event: "Responses\\ResponseVoted",
        callback: async ({question, response}: {question: number; response: number}) => {
            if (question === props.question.id) {
                const {data} = await getGroupQuestionResponseVotes(
                    $route.params.group,
                    $route.params.question,
                    response,
                );
                emit("votesChanged", {response, data});
            }
        },
    },
]);

defineExpose({
    responseRefs,
    pageHeadings,
});
</script>
<template>
    <div>
        <div class="mb-3 flex items-center justify-start">
            <button
                @click.stop="expandAllComments = !expandAllComments"
                class="flex max-w-max gap-1 !rounded border bg-stone-50 !px-2 !py-0.5 text-center text-xs shadow-sm !ring-0 dark:border-stone-700 dark:bg-stone-800 dark:shadow-black"
            >
                {{ expandAllComments ? "Collapse" : "Expand" }} all comments
                <Icon
                    :icon="expandAllComments ? 'akar-icons:chevron-up' : 'akar-icons:chevron-down'"
                    width="16"
                    height="16"
                />
            </button>
        </div>
        <div v-if="!!props.question?.responses?.length" v-auto-animate>
            <template v-if="sort === 'page' && question.pageCount > 1">
                <div class="space-y-2">
                    <template v-for="(group, index) in responses" :key="index">
                        <div class="space-y-2 overflow-y-hidden" v-auto-animate>
                            <button
                                class="flex w-full items-center justify-between rounded-md bg-stone-200 px-2 py-1 text-sm font-medium dark:bg-stone-800 dark:text-white"
                                ref="pageHeadings"
                                @click="collapsed[index] = !collapsed[index]"
                            >
                                <span>Page {{ +index + 1 }}</span>
                                <Icon
                                    :icon="collapsed[index] ? 'akar-icons:chevron-down' : 'akar-icons:chevron-up'"
                                    width="16"
                                    height="16"
                                />
                            </button>
                            <div v-if="group.length && !collapsed[index]" class="space-y-2">
                                <template v-for="response in group" :key="response.id">
                                    <VotingResponse
                                        ref="responseRefs"
                                        :response="response"
                                        :question="question"
                                        :expand-comments="expandAllComments"
                                        :highlight-comment-id="
                                            responseWithHighlightedComment === response.id ? highlightCommentId : null
                                        "
                                        @vote="vote"
                                        @responseClick="emit('responseClick', $event)"
                                        @edit="emit('edit', $event)"
                                        @delete="emit('delete', $event)"
                                        :disabled="disabled"
                                        @comment="emit('comment', {comment: $event, response})"
                                        @deleteComment="emit('deleteComment', {comment: $event, response})"
                                    />
                                </template>
                            </div>
                        </div>
                    </template>
                </div>
            </template>
            <template v-else>
                <div class="space-y-2">
                    <template v-for="response in responses" :key="response.id">
                        <VotingResponse
                            ref="responseRefs"
                            :response="response"
                            :question="question"
                            :expand-comments="expandAllComments"
                            :highlight-comment-id="
                                responseWithHighlightedComment === response.id ? highlightCommentId : null
                            "
                            @vote="vote"
                            @responseClick="emit('responseClick', $event)"
                            @edit="emit('edit', $event)"
                            @delete="emit('delete', $event)"
                            :disabled="disabled"
                            @comment="emit('comment', {comment: $event, response})"
                            @deleteComment="emit('deleteComment', {comment: $event, response})"
                        />
                    </template>
                </div>
            </template>
        </div>
        <div v-else class="w-full p-8">
            <p class="text-center text-stone-500">
                {{ hasFilters ? "No responses found for these filters." : "No responses yet." }}
            </p>
        </div>
    </div>
</template>
